import { AUTH_ROUTES } from "@jipraks/shared";

type FetchLike = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

// One refresh in flight at a time: parallel 401s share it instead of rotating the token twice
// (the second rotation would present an already-rotated token and log the user out).
let pendingRefresh: Promise<boolean> | null = null;

async function refreshSession(): Promise<boolean> {
  try {
    const response = await fetch(AUTH_ROUTES.REFRESH, { method: "POST", credentials: "include" });
    return response.ok;
  } catch (error) {
    console.warn("Session refresh failed", error);
    return false;
  }
}

function requestPath(input: RequestInfo | URL): string {
  const url = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
  return new URL(url, window.location.origin).pathname;
}

/**
 * fetch that survives access-token expiry: on a 401 it refreshes the session once and retries.
 * Auth endpoints themselves are never retried.
 */
export const authFetch: FetchLike = async (input, init) => {
  const response = await fetch(input, { credentials: "include", ...init });
  if (response.status !== 401 || requestPath(input).startsWith("/api/auth/")) return response;

  pendingRefresh ??= refreshSession().finally(() => {
    pendingRefresh = null;
  });
  const isRefreshed = await pendingRefresh;
  return isRefreshed ? fetch(input, { credentials: "include", ...init }) : response;
};
